import React, { useState, useEffect } from "react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from "recharts";
import { ArrowLeft, Download, TrendingUp, BarChart3 } from "lucide-react";
import axiosInstance from "../axiosInstance";
import { useNavigate } from "react-router-dom";


const SalesGrowth = () => {
    const navigate = useNavigate();
  const goBack = () => navigate("/admin/reports");
    const [period, setPeriod] = useState("daily");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [weekStartDate, setWeekStartDate] = useState("");
    const [weekEndDate, setWeekEndDate] = useState("");
    const [startMonth, setStartMonth] = useState("");
    const [endMonth, setEndMonth] = useState("");
    const [startYear, setStartYear] = useState("");
    const [endYear, setEndYear] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    // Helper function to get the Monday of a given date
    const getMondayOfWeek = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(d.setDate(diff));
    };

    // Helper function to get the Sunday of a given date
    const getSundayOfWeek = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? 0 : 7);
        return new Date(d.setDate(diff));
    };

    // Set default dates
    useEffect(() => {
        const today = new Date();
        const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

        setEndDate(today.toISOString().split("T")[0]);
        setStartDate(thirtyDaysAgo.toISOString().split("T")[0]);

        // Set default week dates
        const mondayOfToday = getMondayOfWeek(today);
        const sundayOfToday = getSundayOfWeek(today);
        setWeekStartDate(mondayOfToday.toISOString().split("T")[0]);
        setWeekEndDate(today.toISOString().split("T")[0]); // Set to today, not Sunday

        const currentMonth = today.toISOString().split("T")[0].slice(0, 7);
        const lastMonth = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000)
            .toISOString()
            .split("T")[0]
            .slice(0, 7);
        setEndMonth(currentMonth);
        setStartMonth(lastMonth);

        const currentYear = today.getFullYear().toString();
        setEndYear(currentYear);
        setStartYear((currentYear - 1).toString());
    }, []);

    const handleWeekStartDateChange = (e) => {
        const date = new Date(e.target.value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Don't allow future dates
        if (date > today) {
            setError("Start date cannot be in the future");
            return;
        }

        // Don't allow start date to be greater than end date
        if (weekEndDate && date > new Date(weekEndDate)) {
            setError("Start date cannot be greater than end date");
            return;
        }

        const monday = getMondayOfWeek(date);
        setWeekStartDate(monday.toISOString().split("T")[0]);
        setError("");
    };

    const handleWeekEndDateChange = (e) => {
        const date = new Date(e.target.value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Don't allow future dates
        if (date > today) {
            setError("End date cannot be in the future");
            return;
        }

        // Don't allow end date to be less than start date
        if (weekStartDate && date < new Date(weekStartDate)) {
            setError("End date cannot be less than start date");
            return;
        }

        // If end date is today, don't convert to Sunday, keep it as today
        if (date.toISOString().split("T")[0] === today.toISOString().split("T")[0]) {
            setWeekEndDate(today.toISOString().split("T")[0]);
        } else {
            const sunday = getSundayOfWeek(date);
            setWeekEndDate(sunday.toISOString().split("T")[0]);
        }

        setError("");
    };

    const handleDailyStartDateChange = (e) => {
        const selectedDate = new Date(e.target.value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
            setError("Start date cannot be in the future");
            return;
        }
        if (endDate && selectedDate > new Date(endDate)) {
            setError("Start date cannot be greater than end date");
            return;
        }
        setStartDate(e.target.value);
        setError("");
    };

    const handleDailyEndDateChange = (e) => {
        const selectedDate = new Date(e.target.value);
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
            setError("End date cannot be in the future");
            return;
        }
        if (startDate && selectedDate < new Date(startDate)) {
            setError("End date cannot be less than start date");
            return;
        }
        setEndDate(e.target.value);
        setError("");
    };

    const fetchSalesData = async () => {
        if (loading) return;

        // Validation
        if (period === "daily" && (!startDate || !endDate)) {
            setError("Please select start and end dates");
            return;
        }
        if (period === "weekly" && (!weekStartDate || !weekEndDate)) {
            setError("Please select start and end dates for the week");
            return;
        }
        if (period === "monthly" && (!startMonth || !endMonth)) {
            setError("Please select start and end months");
            return;
        }
        if (period === "yearly" && (!startYear || !endYear)) {
            setError("Please select start and end years");
            return;
        }

        setLoading(true);
        setError("");

        try {
            const params = new URLSearchParams({ period: period });

            if (period === "daily") {
                params.append("startDate", startDate);
                params.append("endDate", endDate);
            } else if (period === "weekly") {
                params.append("startDate", weekStartDate);
                params.append("endDate", weekEndDate);
            } else if (period === "monthly") {
                params.append("startMonth", startMonth);
                params.append("endMonth", endMonth);
            } else if (period === "yearly") {
                params.append("startYear", startYear);
                params.append("endYear", endYear);
            }

            const response = await axiosInstance.get(`/admin/sales-report/growth?${params.toString()}`);

            if (response.status !== 200) {
                throw new Error("Failed to fetch sales data");
            }

            const chartData = response.data.data || [];
            const transformedData = chartData.map(item => ({
                ...item,
                period: period === "weekly"
                    ? `Week of ${new Date(item.period).toLocaleDateString('en-US', {
                        month: '2-digit',
                        day: '2-digit',
                        year: 'numeric'
                    })}`
                    : new Date(item.period).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                    })
            }));

            setData(transformedData);
        } catch (err) {
            setError(err.message || "Error fetching sales data");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white p-4 border-2 border-purple-200 rounded-xl shadow-lg">
                    <p className="font-bold text-gray-800 text-sm mb-2" style={{ fontFamily: "Bambino" }}>{payload[0].payload.period}</p>
                    {payload.map((entry, index) => {
                        const value = parseFloat(entry.value);
                        const displayValue = isFinite(value) ? value.toFixed(2) : 'N/A';
                        return (
                            <p key={index} style={{ color: entry.color, fontFamily: "Bambino" }} className="text-sm font-medium" >
                                {entry.name}: £{displayValue}
                            </p>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 px-4 py-2">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={goBack}
                        className="group flex items-center gap-2 text-gray-600 hover:text-purple-600 transition-all mb-6 font-medium"
                        style={{ fontFamily: "Bambino" }}
                    >
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                        <span>Back to Reports</span>
                    </button>

                    <div className="flex items-center justify-between gap-2">
                        <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-500 to-blue-500 bg-clip-text text-transparent" style={{ fontFamily: "Bambino", lineHeight: "1.2" }}>
                            Sales Growth Analysis
                        </h1>
                        <BarChart3 className="w-12 h-12 text-purple-500" />
                    </div>
                </div>

                {/* Period Selector and Controls */}
                <div className="bg-white/70 backdrop-blur-md rounded-3xl p-6 shadow-xl mb-8 border-2 border-purple-100">
                    <div className="flex flex-wrap gap-3 justify-center mb-8">
                        {["daily", "weekly", "monthly", "yearly"].map((p) => (
                            <button
                                key={p}
                                onClick={() => {
                                    setPeriod(p);
                                    setData([]);
                                    setError("");
                                }}
                                className={`px-6 py-3 rounded-xl font-semibold transition-all ${period === p
                                    ? "bg-gradient-to-r from-purple-400 to-pink-400 text-white shadow-lg"
                                    : "bg-white text-gray-700 border-2 border-purple-200 hover:border-purple-400"
                                    }`}
                                style={{ fontFamily: "Bambino" }}
                            >
                                {p.charAt(0).toUpperCase() + p.slice(1)}
                            </button>
                        ))}
                    </div>

                    {/* Date Range Inputs */}
                    <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-3 mb-8">
                        {period === "daily" && (
                            <>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Start Date:</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={handleDailyStartDateChange}
                                        max={new Date().toISOString().split("T")[0]}
                                        className="border-2 border-purple-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white w-full sm:w-auto"
                                    />
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>End Date:</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={handleDailyEndDateChange}
                                        max={new Date().toISOString().split("T")[0]}
                                        className="border-2 border-purple-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white w-full sm:w-auto"
                                    />
                                </div>
                            </>
                        )}

                        {period === "weekly" && (
                            <>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Start Date (Monday):</label>
                                    <input
                                        type="date"
                                        value={weekStartDate}
                                        onChange={handleWeekStartDateChange}
                                        max={new Date().toISOString().split("T")[0]}
                                        className="border-2 border-purple-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white w-full sm:w-auto"
                                    />
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>End Date (Sunday/Today):</label>
                                    <input
                                        type="date"
                                        value={weekEndDate}
                                        onChange={handleWeekEndDateChange}
                                        max={new Date().toISOString().split("T")[0]}
                                        className="border-2 border-purple-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white w-full sm:w-auto"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 w-full text-center" style={{ fontFamily: "Bambino" }}>Start date adjusts to Monday. End date adjusts to Sunday (or stays as today if selected)</p>
                            </>
                        )}

                        {period === "monthly" && (
                            <>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Start Month:</label>
                                    <input type="month" value={startMonth} onChange={(e) => setStartMonth(e.target.value)} className="border-2 border-purple-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white w-full sm:w-auto" />
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>End Month:</label>
                                    <input type="month" value={endMonth} onChange={(e) => setEndMonth(e.target.value)} className="border-2 border-purple-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white w-full sm:w-auto" />
                                </div>
                            </>
                        )}

                        {period === "yearly" && (
                            <>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>Start Year:</label>
                                    <input type="number" min="2020" max={new Date().getFullYear()} value={startYear} onChange={(e) => setStartYear(e.target.value)} className="border-2 border-purple-200 rounded-lg px-3 py-2 w-24 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white" />
                                </div>
                                <div className="flex flex-col sm:flex-row items-center gap-2">
                                    <label className="font-semibold text-gray-700 text-sm whitespace-nowrap" style={{ fontFamily: "Bambino" }}>End Year:</label>
                                    <input type="number" min="2020" max={new Date().getFullYear()} value={endYear} onChange={(e) => setEndYear(e.target.value)} className="border-2 border-purple-200 rounded-lg px-3 py-2 w-24 focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent transition-all bg-white" />
                                </div>
                            </>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button onClick={fetchSalesData} disabled={loading} className={`px-8 py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${loading ? "bg-gray-400 cursor-not-allowed text-white" : "bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105"}`} style={{ fontFamily: "Bambino" }}>
                            <TrendingUp className="w-5 h-5" />
                            {loading ? "Loading..." : "Fetch Data"}
                        </button>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mt-6 p-4 bg-red-100 border-2 border-red-400 text-red-700 rounded-xl font-medium" style={{ fontFamily: "Bambino" }}>
                            {error}
                        </div>
                    )}
                </div>

                {/* Chart */}
                {data.length > 0 && (
                    <div className="bg-white/70 backdrop-blur-md rounded-3xl p-4 md:p-6 shadow-xl border-2 border-blue-100 overflow-x-auto">
                        <h2 className="text-xl md:text-2xl font-bold text-gray-800 mb-4 md:mb-6 flex items-center gap-3" style={{ fontFamily: "Bambino" }}>
                            <BarChart3 className="w-6 h-6 md:w-8 md:h-8 text-blue-500" />
                            Sales Trend
                        </h2>
                        <div className="w-full min-w-max md:min-w-0">
                            <ResponsiveContainer width={typeof window !== 'undefined' && window.innerWidth < 768 ? Math.max(window.innerWidth - 40, 300) : "100%"} height={window.innerWidth < 768 ? 300 : 400}>
                                <BarChart data={data} margin={{ top: 5, right: 20, left: window.innerWidth < 768 ? -10 : 0, bottom: window.innerWidth < 768 ? 60 : 5 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                    <XAxis
                                        dataKey="period"
                                        tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                                        angle={period === "daily" ? -45 : 0}
                                        textAnchor={period === "daily" ? "end" : "middle"}
                                        height={period === "daily" ? (window.innerWidth < 768 ? 60 : 80) : 40}
                                    />
                                    <YAxis
                                        label={{ value: "Sales (£)", angle: -90, position: "insideLeft", offset: 10 }}
                                        tick={{ fontSize: window.innerWidth < 768 ? 10 : 12 }}
                                        width={window.innerWidth < 768 ? 40 : 60}
                                    />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend
                                        wrapperStyle={{ paddingTop: "20px", fontFamily: "Bambino" }}
                                        wrapperClassName="text-xs md:text-sm"
                                    />
                                    <Bar
                                        dataKey="total_sales"
                                        fill="#8B5CF6"
                                        name="Total Sales"
                                        radius={[8, 8, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="total_sales_website"
                                        fill="#10B981"
                                        name="Website Sales"
                                        radius={[8, 8, 0, 0]}
                                    />
                                    <Bar
                                        dataKey="total_sales_pos"
                                        fill="#F59E0B"
                                        name="POS Sales"
                                        radius={[8, 8, 0, 0]}
                                    />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Empty State */}
                {!loading && data.length === 0 && !error && (
                    <div className="bg-white/70 backdrop-blur-md rounded-3xl p-12 shadow-xl text-center border-2 border-blue-100">
                        <BarChart3 className="w-16 h-16 text-blue-300 mx-auto mb-4" />
                        <p className="text-gray-500 text-lg" style={{ fontFamily: "Bambino" }}>
                            Select dates and click "Fetch Data" to view sales growth trends
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SalesGrowth;